import { useState, useCallback } from 'react'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { useContract } from './useContract.ts'
import { useWallet } from './useWallet.ts'
import { contractConfig } from '@/services/contract/contract.config.ts'
import type {
  TokenInfo,
  ContractWriteResult,
} from '@/services/contract/contract.types.ts'

interface UsePaymentTokenState {
  tokenInfo: TokenInfo | null
  balance: bigint | null
  allowance: bigint | null
  loading: boolean
  error: string | null
}

interface UsePaymentTokenReturn extends UsePaymentTokenState {
  fetchTokenInfo: () => Promise<void>
  fetchBalance: () => Promise<void>
  fetchAllowance: () => Promise<void>
  approve: (amount: bigint) => Promise<ContractWriteResult | null>
  hasEnoughAllowance: (amount: bigint) => boolean
}

export function usePaymentToken(): UsePaymentTokenReturn {
  const contract = useContract()
  const { address } = useWallet()

  const [state, setState] = useState<UsePaymentTokenState>({
    tokenInfo: null,
    balance: null,
    allowance: null,
    loading: false,
    error: null,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))

  const fetchTokenInfo = useCallback(async () => {
    setLoading()
    try {
      const tokenInfo = await contract.getTokenInfo()
      setState((s) => ({ ...s, tokenInfo, loading: false }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch token info')
    }
  }, [contract])

  const fetchBalance = useCallback(async () => {
    if (!address) return
    setLoading()
    try {
      const balance = await contract.getTokenBalance(address as `0x${string}`)
      setState((s) => ({ ...s, balance, loading: false }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch token balance')
    }
  }, [address, contract])

  const fetchAllowance = useCallback(async () => {
    if (!address) return
    setLoading()
    try {
      const allowance = await contract.getTokenAllowance(
        address as `0x${string}`,
        contractConfig.address,
      )
      setState((s) => ({ ...s, allowance, loading: false }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch allowance')
    }
  }, [address, contract])

  const approve = useCallback(
    async (amount: bigint): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.approveToken(
          contractConfig.address,
          amount,
        )
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const hasEnoughAllowance = useCallback(
    (amount: bigint): boolean => {
      if (state.allowance === null) return false
      return state.allowance >= amount
    },
    [state.allowance],
  )

  return {
    ...state,
    fetchTokenInfo,
    fetchBalance,
    fetchAllowance,
    approve,
    hasEnoughAllowance,
  }
}
